/* GZ core: product info, variant picker, product form, quantity input.
   Clean-room implementation for the ported Gardenizi sections on Dawn (see js/CONTRACT.md). */
(() => {
  const GZ = (window.GZ = window.GZ || {});

  GZ.moneyFormat = window.GZ_MONEY_FORMAT || '${{amount}}';
  GZ.define = (name, cls) => {
    if (!customElements.get(name)) customElements.define(name, cls);
  };
  GZ.qs = (sel, root = document) => root.querySelector(sel);
  GZ.qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  GZ.emit = (name, detail, target = document) => {
    const evt = new CustomEvent(name, { bubbles: true, cancelable: true, detail });
    target.dispatchEvent(evt);
    return evt;
  };
  GZ.fetchSection = (url) =>
    fetch(url)
      .then((r) => r.text())
      .then((text) => new DOMParser().parseFromString(text, 'text/html'));

  GZ.formatMoney = (cents, format = GZ.moneyFormat) => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = parseInt(cents, 10) || 0;
    const fmt = (n, decimals, thousands = ',', decimal = '.') => {
      const fixed = (n / 100).toFixed(decimals);
      const [int, frac] = fixed.split('.');
      return int.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`) + (frac ? decimal + frac : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return fmt(cents, 0);
        case 'amount_with_comma_separator': return fmt(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(cents, 2, "'", '.');
        case 'amount_with_space_separator': return fmt(cents, 2, ' ', ',');
        default: return fmt(cents, 2);
      }
    });
  };

  // Copy attributes + children of `fresh` onto `current` (keeps the node, so listeners on it survive).
  GZ.swap = (current, fresh) => {
    if (!current || !fresh) return;
    Array.from(current.attributes).forEach((a) => {
      if (!fresh.hasAttribute(a.name)) current.removeAttribute(a.name);
    });
    Array.from(fresh.attributes).forEach((a) => current.setAttribute(a.name, a.value));
    current.innerHTML = fresh.innerHTML;
  };

  const strings = () => window.variantStrings || { addToCart: 'Add to cart', soldOut: 'Sold out', unavailable: 'Unavailable' };

  /* ---------------- gz-variant-selects ---------------- */
  // Regions re-rendered from the server on variant change, by id prefix + section id.
  const SWAP_IDS = [
    'price-', 'Sku-', 'Inventory-', 'main-atc-price-', 'sticky-atc-price-', 'sticky-atc-separate-price-',
    'Share-', // quantity-breaks / gifts / bundle regions are refreshed by extras.js to keep selections
  ];

  class GzVariantSelects extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.sectionId = this.dataset.section;
      this.addEventListener('change', (e) => this.onChange(e));
    }

    get variants() {
      if (!this._variants) {
        const json = this.querySelector('script[type="application/json"]');
        this._variants = json ? JSON.parse(json.textContent) : [];
      }
      return this._variants;
    }

    selectedOptions() {
      return GZ.qsa('.product-form__input', this).map((wrap) => {
        const select = wrap.querySelector('select');
        if (select) return select.value;
        const checked = wrap.querySelector('input[type="radio"]:checked');
        return checked ? checked.value : null;
      });
    }

    findVariant(options) {
      return this.variants.find((v) => v.options.every((val, i) => options[i] === undefined || options[i] === val)) || null;
    }

    onChange(event) {
      if (event.target.closest('gz-quantity-breaks') && event.target.name === 'quantity') return;
      const options = this.selectedOptions();
      let variant = this.findVariant(options);
      if ((!variant || !variant.available) && this.dataset.skipUnavailable === 'true') {
        // keep the option the user just picked, pick the first available match for the rest
        const changedIndex = GZ.qsa('.product-form__input', this).findIndex((w) => w.contains(event.target));
        const alt = this.variants.find((v) => v.available && v.options[changedIndex] === options[changedIndex]);
        if (alt) {
          variant = alt;
          this.applyOptions(alt.options);
        }
      }
      this.currentVariant = variant;
      this.updateLabels(options);
      this.updateInputs(variant);
      if (!variant) return this.setUnavailable();
      this.updateUrl(variant);
      this.render(variant);
    }

    applyOptions(values) {
      GZ.qsa('.product-form__input', this).forEach((wrap, i) => {
        const select = wrap.querySelector('select');
        if (select) select.value = values[i];
        else {
          const input = GZ.qsa('input[type="radio"]', wrap).find((r) => r.value === values[i]);
          if (input) input.checked = true;
        }
      });
    }

    updateLabels(options) {
      // custom labels support [selected]; server re-render handles the text, nothing to do client side
      this.dataset.selected = options.join(' / ');
    }

    updateInputs(variant) {
      const formId = `product-form-${this.sectionId}`;
      const sel = `#${formId} input[name="id"], input[name="id"][form="${formId}"], #product-form-installment-${this.sectionId} input[name="id"]`;
      GZ.qsa(sel).forEach((input) => {
        input.value = variant ? variant.id : '';
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    updateUrl(variant) {
      if (this.dataset.updateUrl === 'false' || !this.dataset.url) return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${variant.id}`);
    }

    setUnavailable() {
      GZ.qsa(`#ProductSubmitButton-${this.sectionId}, #SectionAtcBtn-${this.sectionId}`).forEach((btn) => {
        btn.setAttribute('disabled', 'disabled');
        const text = btn.querySelector('.main-atc__label__text') || btn.querySelector('.button__label');
        if (text && btn.id.startsWith('ProductSubmitButton')) text.textContent = strings().unavailable;
      });
      GZ.qsa(`#price-${this.sectionId}, #main-atc-price-${this.sectionId}`).forEach((el) => el.classList.add('visibility-hidden'));
      GZ.emit('gz:variant-change', { sectionId: this.sectionId, variant: null, html: null });
    }

    render(variant) {
      const sid = this.sectionId;
      const requestId = (this._requestId = (this._requestId || 0) + 1);
      const base = this.dataset.url || window.location.pathname;
      GZ.fetchSection(`${base}?variant=${variant.id}&section_id=${this.dataset.originalSection || sid}`)
        .then((html) => {
          if (requestId !== this._requestId) return;
          SWAP_IDS.forEach((prefix) => {
            const id = prefix + sid;
            GZ.swap(document.getElementById(id), html.getElementById(id));
          });
          GZ.qsa(`[id^="custom-label-${sid}-"]`).forEach((el) => GZ.swap(el, html.getElementById(el.id)));
          this.updateOptionStates(html);
          this.updateButtons(html, variant);
          this.updateQuantityRules(html);
          GZ.qsa(`#price-${sid}, #main-atc-price-${sid}`).forEach((el) => el.classList.remove('visibility-hidden'));
          GZ.emit('gz:variant-change', { sectionId: sid, variant, html });
          if (window.publish && window.PUB_SUB_EVENTS) {
            window.publish(window.PUB_SUB_EVENTS.variantChange, { data: { sectionId: sid, html, variant } });
          }
        })
        .catch((e) => console.error('[gz] variant render failed', e));
    }

    // Refresh disabled/unavailable markers on option values without rebuilding the picker.
    updateOptionStates(html) {
      const fresh = html.getElementById(this.id);
      if (!fresh) return;
      GZ.qsa('input[type="radio"], option', this).forEach((el) => {
        const twin = el.id ? fresh.querySelector(`#${CSS.escape(el.id)}`) : null;
        const match = twin || (el.tagName === 'OPTION'
          ? GZ.qsa('option', fresh).find((o) => o.value === el.value && o.parentElement.id === el.parentElement.id)
          : null);
        if (!match) return;
        el.className = match.className;
        if (el.tagName === 'OPTION') el.textContent = match.textContent;
      });
    }

    updateButtons(html, variant) {
      const sid = this.sectionId;
      [`ProductSubmitButton-${sid}`, `SectionAtcBtn-${sid}`].forEach((id) => {
        const btn = document.getElementById(id);
        const fresh = html.getElementById(id);
        if (!btn) return;
        const disabled = fresh ? fresh.hasAttribute('disabled') : !variant.available;
        btn.toggleAttribute('disabled', disabled);
        btn.toggleAttribute('data-unavailable', disabled);
        const text = btn.querySelector('.main-atc__label__text');
        const freshText = fresh && fresh.querySelector('.main-atc__label__text');
        if (text && freshText) text.innerHTML = freshText.innerHTML;
      });
    }

    updateQuantityRules(html) {
      const id = `Quantity-${this.sectionId}`;
      const input = document.getElementById(id);
      const fresh = html.getElementById(id);
      if (!input || !fresh) return;
      ['min', 'max', 'step', 'data-min', 'data-max', 'data-cart-quantity'].forEach((attr) => {
        if (fresh.hasAttribute(attr)) input.setAttribute(attr, fresh.getAttribute(attr));
        else input.removeAttribute(attr);
      });
    }
  }
  GZ.define('gz-variant-selects', GzVariantSelects);

  /* ---------------- gz-product-info ---------------- */
  // Container only: variant logic lives in gz-variant-selects; quantity changes are re-broadcast.
  class GzProductInfo extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.sectionId = this.dataset.section;
      this.addEventListener('change', (e) => {
        if (e.target.name === 'quantity' && e.target.matches('.quantity__input, input[type="radio"]')) {
          GZ.emit('gz:quantity-change', { sectionId: this.sectionId, quantity: parseInt(e.target.value, 10) || 1 });
        }
      });
    }
  }
  GZ.define('gz-product-info', GzProductInfo);

  /* ---------------- gz-quantity-input ---------------- */
  class GzQuantityInput extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.input = this.querySelector('input');
      if (!this.input) return;
      this.querySelectorAll('button[name="plus"], button[name="minus"]').forEach((b) =>
        b.addEventListener('click', (e) => this.onButton(e))
      );
      this.input.addEventListener('change', () => this.validate());
      this.validate();
    }

    onButton(event) {
      event.preventDefault();
      const prev = this.input.value;
      const btn = event.currentTarget;
      if (btn.name === 'plus') {
        const min = parseInt(this.input.dataset.min, 10);
        if (min > parseInt(this.input.step || 1, 10) && Number(this.input.value) === 0) this.input.value = min;
        else this.input.stepUp();
      } else {
        this.input.stepDown();
      }
      if (prev !== this.input.value) this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    validate() {
      const value = parseInt(this.input.value, 10);
      const minus = this.querySelector('button[name="minus"]');
      const plus = this.querySelector('button[name="plus"]');
      if (minus && this.input.min) minus.classList.toggle('disabled', value <= parseInt(this.input.min, 10));
      if (plus && this.input.max) plus.classList.toggle('disabled', value >= parseInt(this.input.max, 10));
    }
  }
  GZ.define('gz-quantity-input', GzQuantityInput);

  /* ---------------- gz-product-form ---------------- */
  class GzProductForm extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this.form = this.querySelector('form');
      if (!this.form) return;
      this._bound = true;
      this.sectionId = this.dataset.section;
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
      const variantInput = this.form.querySelector('input[name="id"]');
      if (variantInput) variantInput.disabled = false;
      // "buy now" style secondary button: add, then go straight to checkout
      this.querySelectorAll('[data-skip-cart-button]').forEach((btn) =>
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.skipCart = true;
          this.form.requestSubmit ? this.form.requestSubmit() : this.form.dispatchEvent(new Event('submit', { cancelable: true }));
        })
      );
    }

    get cart() {
      return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    }

    get submitButton() {
      return this.form.querySelector('[type="submit"]') || document.querySelector(`[type="submit"][form="${this.form.getAttribute('id')}"]`);
    }

    setLoading(on) {
      const buttons = [this.submitButton, ...this.querySelectorAll('[data-skip-cart-button]')].filter(Boolean);
      buttons.forEach((btn) => {
        btn.classList.toggle('loading', on);
        btn.toggleAttribute('aria-disabled', on);
        const spinner = btn.querySelector('.loading-overlay__spinner, .loading__spinner');
        if (spinner) spinner.classList.toggle('hidden', !on);
      });
    }

    showError(message) {
      const wrap = this.querySelector('.product-form__error-message-wrapper');
      if (!wrap) {
        if (message) console.warn('[gz] add to cart:', message);
        return;
      }
      wrap.toggleAttribute('hidden', !message);
      const text = wrap.querySelector('.product-form__error-message');
      if (text && message) text.textContent = message;
    }

    onSubmit(event) {
      event.preventDefault();
      const btn = this.submitButton;
      if (btn && (btn.getAttribute('aria-disabled') === 'true' || btn.hasAttribute('disabled'))) return;
      if (btn) {
        const required = parseInt(btn.dataset.requiredFields || '0', 10);
        const valid = parseInt(btn.dataset.validFields || '0', 10);
        if (required > valid) {
          GZ.emit('gz:invalid', { sectionId: this.sectionId }, this);
          this.showError(window.GZ_STRINGS && window.GZ_STRINGS.requiredFields || 'Please fill in all required fields.');
          return;
        }
      }
      this.showError(false);

      const formData = new FormData(this.form);
      const before = GZ.emit('gz:before-add', { sectionId: this.sectionId, formData, items: [] }, this);
      if (before.defaultPrevented) return;
      const extraItems = before.detail.items;
      const skipCart = this.skipCart || !!this.form.querySelector('input[name="id"][data-skip-cart="true"]');
      this.skipCart = false;

      const cart = skipCart ? null : this.cart;
      const sections = cart ? cart.getSectionsToRender().map((s) => s.id) : [];
      const config = window.fetchConfig ? window.fetchConfig('javascript') : { method: 'POST', headers: { Accept: 'application/javascript' } };
      config.headers['X-Requested-With'] = 'XMLHttpRequest';

      if (extraItems.length) {
        const main = { id: formData.get('id'), quantity: parseInt(formData.get('quantity'), 10) || 1 };
        const properties = {};
        for (const [key, value] of formData.entries()) {
          const m = key.match(/^properties\[(.+)\]$/);
          if (m && value !== '' && !(value instanceof File && !value.size)) properties[m[1]] = value;
        }
        if (Object.keys(properties).length) main.properties = properties;
        if (formData.get('selling_plan')) main.selling_plan = formData.get('selling_plan');
        config.headers['Content-Type'] = 'application/json';
        config.body = JSON.stringify({ items: [main, ...extraItems], sections, sections_url: window.location.pathname });
      } else {
        delete config.headers['Content-Type'];
        if (cart) {
          formData.append('sections', sections);
          formData.append('sections_url', window.location.pathname);
        }
        config.body = formData;
      }
      if (cart && cart.setActiveElement) cart.setActiveElement(document.activeElement);

      this.setLoading(true);
      const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
      fetch(addUrl, config)
        .then((r) => r.json())
        .then((response) => {
          if (response.status) {
            this.showError(response.description || response.message);
            if (window.publish && window.PUB_SUB_EVENTS) {
              window.publish(window.PUB_SUB_EVENTS.cartError, { source: 'gz-product-form', errors: response.errors || response.description, message: response.message });
            }
            return;
          }
          // multi-line adds return { items, sections }; Dawn's cart UI expects the single-item shape
          if (response.items && !response.key && response.items.length) {
            Object.assign(response, { key: response.items[0].key, id: response.items[0].id });
          }
          GZ.emit('gz:cart-updated', { response }, this);
          if (skipCart) {
            window.location = '/checkout';
            return;
          }
          if (!cart) {
            window.location = (window.routes && window.routes.cart_url) || '/cart';
            return;
          }
          if (window.publish && window.PUB_SUB_EVENTS) {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'gz-product-form', productVariantId: formData.get('id'), cartData: response });
          }
          const modal = this.closest('gz-quick-add-modal, quick-add-modal');
          if (modal && modal.hide) {
            document.body.addEventListener('modalClosed', () => setTimeout(() => cart.renderContents(response)), { once: true });
            modal.hide(true);
          } else {
            try {
              cart.renderContents(response);
            } catch (e) {
              console.error('[gz] cart UI refresh failed', e); // item is in the cart already
            }
          }
          if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
        })
        .catch((e) => {
          console.error('[gz] add to cart failed', e);
          this.showError('Network error, please try again.');
        })
        .finally(() => this.setLoading(false));
    }
  }
  GZ.define('gz-product-form', GzProductForm);
})();
